import { useCallback, useMemo, useState } from "react";
import { useShellRouter } from "@hooks/useShellAwareRouter";
import dayjs from "dayjs";
import { deviceDetector, OspToast } from "@ospweb/sdk-ui";
import { EdgenDrawerCallable } from "../components/EdgenDrawerCallable";
import { useMemberUser, type MemberPlanInfo } from "./useMemberUser";
import { useMemberFeature } from "./useMemberFeature";
import {
  getPaymentCheckErrorMessage,
  useAppPurchaseFlow,
} from "./useAppPurchaseFlow";
import { usePayment } from "./usePayment";
import {
  getChatMemberStatus,
  type ChatMemberStatus,
  type ChatMemberStatusCardProps,
} from "@components/Chat/MemberStatusCard";
import { ROUTER_PATH } from "@config/router";
import {
  ProductPayCycle,
  ProductStatus,
  ProductType,
} from "@components/Member/CurrentPlan/constants";
import { MemberProductLevelEnum } from "@features/api/memberApi";
import { EdgenModalCreateAble } from "../components/EdgenModal";
import { invalidateMemberQueries } from "@utils/memberInvalidateQueries";
import { useQueryClient } from "@tanstack/react-query";

export interface UseChatMemberStatusCardReturn {
  /** 用户状态: free/pro/expert/trial/complete */
  memberStatus: ChatMemberStatus;
  /** MemberStatusCard 所需的完整 props */
  cardProps: Omit<ChatMemberStatusCardProps, "memberStatus">;
  /** 原始会员信息 */
  memberInfo: MemberPlanInfo | undefined;
  /** 原始功能信息 */
  memberFeatureData: any;
  /** 是否为试用用户 */
  isTrial: boolean;
  /** 试用价格列表 */
  trialList: any[];
  /** 设备是否为 iOS */
  isInIOS: boolean;
  /** 加载状态 */
  isLoading: boolean;
  /** 是否正在进行应用内续费支付 */
  isAppPayingRenewLoading: boolean;
}

/**
 * Hook：整合会员信息与试用数据，返回 MemberStatusCard 所需的 props
 *
 * 业务逻辑：
 * - free用户: 优先展示试用产品，否则使用年付Pro
 * - pro用户: 传递月付Expert产品信息
 * - expert用户: 点击Go按钮去News页面
 * - secondary按钮: 所有状态都去Pricing页面
 *
 * @example
 * const { memberStatus, cardProps, isTrial, isLoading } = useChatMemberStatusCard();
 *
 * return (
 *   <MemberStatusCard memberStatus={memberStatus} {...cardProps} />
 * );
 */
export const useChatMemberStatusCard = (): UseChatMemberStatusCardReturn => {
  const router = useShellRouter();
  const {
    data: memberInfo,
    isLoading: isLoadingMember,
    refetch: refetchMemberUser,
  } = useMemberUser();
  const {
    data: memberFeatureData,
    isTrial: isTrialFromFeature,
    isLoading: isLoadingFeature,
    trialList = [],
  } = useMemberFeature();
  const [isAppPayingRenewLoading, setIsAppPayingRenewLoading] = useState(false);
  const { purchaseInApp, shouldTryNativePayment } = useAppPurchaseFlow();
  const { handlePaymentCheck } = usePayment();
  const queryClient = useQueryClient();

  const onPaymentCheck = useCallback(
    async (planId: string) => {
      const { data: paypalData, error } = await handlePaymentCheck(planId);
      if (!error) {
        window.location.href = (paypalData as string) || "";
      } else {
        const errMsg = getPaymentCheckErrorMessage({
          code: error.code,
          msg: error.msg,
        });
        OspToast.error({
          content: errMsg,
        });
      }
    },
    [handlePaymentCheck],
  );

  const isInIOS = deviceDetector.isIOSApp();
  const isLoading = isLoadingMember || isLoadingFeature;
  const isTrial = !!isTrialFromFeature;

  const memberStatus = useMemo<ChatMemberStatus>(() => {
    return getChatMemberStatus({
      isPro: memberInfo?.isPro,
      isExpert: memberInfo?.isExpert,
      isTrial: isTrial && !memberInfo?.isPro && !memberInfo?.isExpert,
    });
  }, [memberInfo?.isPro, memberInfo?.isExpert, isTrial]);

  // 查找 Pro 产品（试用匹配与回退购买都使用年付 Pro）
  const { proYearProduct } = useMemo(() => {
    const products = (memberFeatureData || []) as any[];

    const proYear = products.find(
      (item) =>
        item.level === MemberProductLevelEnum.PRO &&
        item.pay_cycle === ProductPayCycle.YEAR &&
        item.status === ProductStatus.ACTIVE &&
        item.type === ProductType.NORMAL,
    );

    return {
      proYearProduct: proYear,
    };
  }, [memberFeatureData]);

  // 试用产品仅绑定年付 Pro（proYearDisplayProduct），按 proYearProduct.id 精确匹配
  const matchedTrialProduct = useMemo(() => {
    if (!proYearProduct?.id || !trialList?.length) {
      return undefined;
    }
    return trialList.find((item: any) => item.renewal_id === proYearProduct.id);
  }, [proYearProduct?.id, trialList]);

  const trialPrice = matchedTrialProduct?.price;

  const resumeTimestamp = useCallback(() => {
    const timestamp = memberInfo?.credit?.end_time || memberInfo?.expire_time;

    if (!timestamp) {
      return "";
    }

    const normalizedTimestamp = timestamp < 1e12 ? timestamp * 1000 : timestamp;
    return dayjs(normalizedTimestamp).utc().format("MMM D [at] h:mmA");
  }, [memberInfo?.credit?.end_time, memberInfo?.expire_time]);

  const resumeTimeText = useMemo(() => {
    // pro/expert 下架 memberStatus 已统一为 complete 状态
    if (memberStatus === "complete") {
      return resumeTimestamp();
    }
    return "";
  }, [memberStatus, resumeTimestamp]);

  // 通用购买处理逻辑
  const handlePurchase = useCallback(
    async (product: any, errorMessage: string = "Product not available") => {
      if (!product) {
        OspToast.error({ content: errorMessage });
        return;
      }

      if (!shouldTryNativePayment) {
        onPaymentCheck(product?.id);
        return;
      }
      if (isAppPayingRenewLoading) {
        return;
      }
      setIsAppPayingRenewLoading(true);
      const result = await purchaseInApp(product, {
        onSuccess: async () => {
          const maxAttempts = 5;
          const intervalMs = 1000;
          let purchased = false;

          for (let attempt = 0; attempt < maxAttempts; attempt++) {
            const result = await refetchMemberUser();
            const latestMemberInfo = result?.data;
            const hasRenewed =
              latestMemberInfo?.auto_renewal &&
              (latestMemberInfo?.product?.renewal_id === product?.id ||
                latestMemberInfo?.product?.id === product?.id);

            if (hasRenewed) {
              purchased = true;
              break;
            }
            if (attempt < maxAttempts - 1) {
              await new Promise((resolve) => setTimeout(resolve, intervalMs));
            }
          }

          setIsAppPayingRenewLoading(false);
          if (purchased) {
            setTimeout(() => {
              EdgenModalCreateAble.end(false);
            }, 500);
            invalidateMemberQueries(queryClient);
            OspToast.success({ content: "Purchase successful!" });
          } else {
            router.push(`${ROUTER_PATH.MEMBER}/?tab=pricing`);
          }
        },
        onError: (msg) => {
          OspToast.error({ content: msg || "Purchase failed" });
        },
      });
      setIsAppPayingRenewLoading(false);
      if (!result.success && result.fallbackToStripe) {
        onPaymentCheck(product?.id);
      }
    },
    [
      shouldTryNativePayment,
      isAppPayingRenewLoading,
      purchaseInApp,
      onPaymentCheck,
      refetchMemberUser,
      queryClient,
      router,
    ],
  );

  // 处理free用户购买（优先试用，否则年付Pro）
  const handleFreeUserPurchase = useCallback(async () => {
    // 优先试用产品，其次年付 Pro 产品
    const productForPurchase = matchedTrialProduct ?? proYearProduct;

    handlePurchase(productForPurchase, "No product available for purchase");
  }, [matchedTrialProduct, proYearProduct, handlePurchase]);

  // Pro/Expert/Complete 都统一跳转到 News
  const handleGoToInvest = useCallback(() => {
    router.push(ROUTER_PATH.INVEST_OPPORTUNITY);
  }, [router]);

  // 处理所有状态的Secondary按钮（去Pricing页面）
  const handleSecondaryClick = useCallback(() => {
    EdgenDrawerCallable.end(false);
    router.push(`${ROUTER_PATH.MEMBER}/?tab=pricing`);
  }, [router]);

  // 根据用户状态返回不同的Primary Click Handler
  const onPrimaryClick = useCallback(() => {
    const actionMap: Record<ChatMemberStatus, () => void> = {
      free: handleFreeUserPurchase,
      trial: handleFreeUserPurchase,
      complete: handleGoToInvest,
    };
    EdgenDrawerCallable.end(false);

    actionMap[memberStatus]?.();
  }, [memberStatus, handleFreeUserPurchase, handleGoToInvest]);

  // 组织 cardProps
  const cardProps = useMemo<
    Omit<ChatMemberStatusCardProps, "memberStatus">
  >(() => {
    // pro/expert 下架 memberStatus 已统一为 complete 状态
    const shouldUseLimitContent = memberStatus === "complete";

    return {
      trialPrice,
      resumeTimeText: resumeTimeText || undefined,
      button: shouldUseLimitContent
        ? {
            primary: {
              text: "Go",
              style: "tonal",
            },
            secondary: {
              visible: false,
            },
          }
        : undefined,
      onPrimaryClick,
      onSecondaryClick: shouldUseLimitContent
        ? undefined
        : handleSecondaryClick,
    };
  }, [
    trialPrice,
    memberStatus,
    resumeTimeText,
    onPrimaryClick,
    handleSecondaryClick,
  ]);

  return {
    memberStatus,
    isAppPayingRenewLoading,
    cardProps,
    memberInfo,
    memberFeatureData,
    isTrial,
    trialList,
    isInIOS,
    isLoading,
  };
};
